import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';


const DevDebug = () => {
  const [logs, setLogs] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [envStatus, setEnvStatus] = useState({});
  const [currentSession, setCurrentSession] = useState(null);
  const [authState, setAuthState] = useState('idle');
  const [testEmail, setTestEmail] = useState(`test_${Date.now()}@example.com`);
  const [testPassword, setTestPassword] = useState('TestPassword123!');
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = useRef(null);

  const addLog = (message, type = 'info', data = null) => {
    const timestamp = new Date()?.toISOString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      type,
      message,
      data
    };
    setLogs(prev => [...prev, logEntry]);
  };

  useEffect(() => {
    if (autoScroll && logsEndRef?.current) {
      logsEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  useEffect(() => {
    checkEnvironment();
    checkConnection();
    checkSession();

    // Subscribe to auth state changes
    const { data: authListener } = supabase?.auth?.onAuthStateChange((event, session) => {
      addLog(`Auth State Changed: ${event}`, 'info', {
        event,
        userId: session?.user?.id,
        email: session?.user?.email
      });
      setCurrentSession(session);
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const checkEnvironment = () => {
    addLog('Checking environment variables...', 'info');
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

    const status = {
      url: !!supabaseUrl,
      key: !!supabaseKey,
      urlValue: supabaseUrl || 'MISSING',
      keyLength: supabaseKey?.length || 0
    };

    setEnvStatus(status);

    if (status?.url && status?.key) {
      addLog('✅ Environment variables configured', 'success', status);
    } else {
      addLog('❌ Missing environment variables', 'error', status);
    }
  };

  const checkConnection = async () => {
    addLog('Testing Supabase connection...', 'info');
    setConnectionStatus('checking');

    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('count', { count: 'exact', head: true });

      if (error) {
        addLog('❌ Connection failed', 'error', { error: error?.message });
        setConnectionStatus('error');
      } else {
        addLog('✅ Successfully connected to Supabase', 'success');
        setConnectionStatus('connected');
      }
    } catch (err) {
      addLog('❌ Connection exception', 'error', { error: err?.message });
      setConnectionStatus('error');
    }
  };

  const checkSession = async () => {
    addLog('Checking current session...', 'info');

    try {
      const { data: { session }, error } = await supabase?.auth?.getSession();

      if (error) {
        addLog('❌ Session check failed', 'error', { error: error?.message });
        return;
      }

      if (session) {
        addLog('✅ Active session found', 'success', {
          userId: session?.user?.id,
          email: session?.user?.email,
          expiresAt: new Date(session.expires_at * 1000)?.toLocaleString()
        });
        setCurrentSession(session);
      } else {
        addLog('ℹ️ No active session', 'info');
        setCurrentSession(null);
      }
    } catch (err) {
      addLog('❌ Session check exception', 'error', { error: err?.message });
    }
  };

  const testRegistration = async () => {
    addLog('=== STARTING REGISTRATION TEST ===', 'info');
    setAuthState('registering');

    addLog(`Step 1: Preparing registration for ${testEmail}`, 'info');

    try {
      addLog('Step 2: Calling Supabase signUp API...', 'info');

      const { data, error } = await supabase?.auth?.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          data: {
            full_name: 'Test User',
            role: 'cliente'
          }
        }
      });

      if (error) {
        addLog('❌ Step 3: Registration failed', 'error', {
          errorCode: error?.code,
          errorMessage: error?.message,
          errorStatus: error?.status
        });
        setAuthState('error');
        return;
      }

      if (data?.user) {
        addLog('✅ Step 3: User created successfully', 'success', {
          userId: data?.user?.id,
          email: data?.user?.email,
          confirmed: data?.user?.confirmed_at ? 'Yes' : 'No'
        });

        if (!data?.user?.confirmed_at) {
          addLog('⚠️ Step 4: Email confirmation required', 'warning', {
            message: 'Check email inbox for confirmation link'
          });
        } else {
          addLog('✅ Step 4: Email already confirmed', 'success');
        }

        setAuthState('registered');
      }
    } catch (err) {
      addLog('❌ Registration exception', 'error', {
        error: err?.message,
        stack: err?.stack
      });
      setAuthState('error');
    }
  };

  const testLogin = async () => {
    addLog('=== STARTING LOGIN TEST ===', 'info');
    setAuthState('logging-in');

    addLog(`Step 1: Preparing login for ${testEmail}`, 'info');

    try {
      addLog('Step 2: Calling Supabase signInWithPassword API...', 'info');

      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: testEmail,
        password: testPassword
      });

      if (error) {
        addLog('❌ Step 3: Login failed', 'error', {
          errorCode: error?.code,
          errorMessage: error?.message,
          errorStatus: error?.status
        });

        if (error?.message?.includes('Email not confirmed')) {
          addLog('⚠️ Diagnosis: Email not confirmed', 'warning', {
            solution: 'User must confirm email before logging in'
          });
        } else if (error?.message?.includes('Invalid login credentials')) {
          addLog('⚠️ Diagnosis: Invalid credentials', 'warning', {
            solution: 'Check email and password are correct'
          });
        }

        setAuthState('error');
        return;
      }

      if (data?.user && data?.session) {
        addLog('✅ Step 3: Login successful', 'success', {
          userId: data?.user?.id,
          email: data?.user?.email
        });

        addLog('✅ Step 4: Session created', 'success', {
          accessToken: data?.session?.access_token?.substring(0, 20) + '...',
          expiresAt: new Date(data.session.expires_at * 1000)?.toLocaleString()
        });

        setCurrentSession(data?.session);
        setAuthState('logged-in');

        // Check user profile
        addLog('Step 5: Checking user profile...', 'info');
        const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', data?.user?.id)?.single();

        if (profileError) {
          addLog('⚠️ Step 6: Profile not found or error', 'warning', {
            error: profileError?.message
          });
        } else {
          addLog('✅ Step 6: Profile loaded', 'success', {
            profile
          });
        }
      }
    } catch (err) {
      addLog('❌ Login exception', 'error', {
        error: err?.message,
        stack: err?.stack
      });
      setAuthState('error');
    }
  };

  const testLogout = async () => {
    addLog('=== STARTING LOGOUT TEST ===', 'info');
    setAuthState('logging-out');

    try {
      addLog('Step 1: Calling Supabase signOut API...', 'info');

      const { error } = await supabase?.auth?.signOut();

      if (error) {
        addLog('❌ Step 2: Logout failed', 'error', {
          errorCode: error?.code,
          errorMessage: error?.message
        });
        setAuthState('error');
        return;
      }

      addLog('✅ Step 2: Logout successful', 'success');
      addLog('✅ Step 3: Session cleared', 'success');
      setCurrentSession(null);
      setAuthState('logged-out');
    } catch (err) {
      addLog('❌ Logout exception', 'error', {
        error: err?.message
      });
      setAuthState('error');
    }
  };

  const clearLogs = () => {
    setLogs([]);
    addLog('Logs cleared', 'info');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'checking': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'info': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '•';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">🔧 Supabase Debug Console</h1>
              <p className="text-gray-600 mt-1">Developer-only real-time auth monitoring</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(connectionStatus)} animate-pulse`}></div>
                <span className="text-sm font-medium text-gray-700">
                  {connectionStatus === 'connected' ? 'Connected' : connectionStatus === 'error' ? 'Disconnected' : 'Checking...'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Panel - Status & Controls */}
          <div className="lg:col-span-1 space-y-4">
            {/* Environment Status */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Environment</h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Supabase URL:</span>
                  <span className={envStatus?.url ? 'text-green-600' : 'text-red-600'}>
                    {envStatus?.url ? '✅' : '❌'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Anon Key:</span>
                  <span className={envStatus?.key ? 'text-green-600' : 'text-red-600'}>
                    {envStatus?.key ? `✅ (${envStatus?.keyLength} chars)` : '❌'}
                  </span>
                </div>
                {envStatus?.urlValue && envStatus?.urlValue !== 'MISSING' && (
                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs break-all">
                    {envStatus?.urlValue}
                  </div>
                )}
              </div>
            </div>

            {/* Current Session */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Current Session</h2>
              {currentSession ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-600 font-medium">Active</span>
                  </div>
                  <div className="text-gray-600">
                    <strong>Email:</strong> {currentSession?.user?.email}
                  </div>
                  <div className="text-gray-600">
                    <strong>User ID:</strong>
                    <div className="text-xs break-all mt-1">{currentSession?.user?.id}</div>
                  </div>
                  <div className="text-gray-600">
                    <strong>Expires:</strong>
                    <div className="text-xs mt-1">
                      {new Date(currentSession.expires_at * 1000)?.toLocaleString()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-gray-600">No active session</span>
                </div>
              )}
            </div>

            {/* Auth State */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Auth State</h2>
              <div className="text-center">
                <div className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-800 font-medium text-sm">
                  {authState}
                </div>
              </div>
            </div>

            {/* Test Controls */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Test Controls</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Test Email</label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Test Password</label>
                  <input
                    type="text"
                    value={testPassword}
                    onChange={(e) => setTestPassword(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <button
                    onClick={testRegistration}
                    disabled={authState === 'registering'}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 text-sm font-medium"
                  >
                    Test Registration
                  </button>
                  <button
                    onClick={testLogin}
                    disabled={authState === 'logging-in'}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 text-sm font-medium"
                  >
                    Test Login
                  </button>
                  <button
                    onClick={testLogout}
                    disabled={authState === 'logging-out' || !currentSession}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 text-sm font-medium"
                  >
                    Test Logout
                  </button>
                </div>
                <div className="pt-2 border-t border-gray-200 space-y-2">
                  <button
                    onClick={checkConnection}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium"
                  >
                    Recheck Connection
                  </button>
                  <button
                    onClick={checkSession}
                    className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium"
                  >
                    Refresh Session
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Logs */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Real-Time Logs</h2>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={autoScroll}
                      onChange={(e) => setAutoScroll(e?.target?.checked)}
                      className="rounded"
                    />
                    Auto-scroll
                  </label>
                  <button
                    onClick={clearLogs}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
                  >
                    Clear Logs
                  </button>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-4 h-[calc(100vh-200px)] overflow-y-auto font-mono text-sm">
                {logs?.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">
                    No logs yet. Run a test to see real-time output.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {logs?.map((log) => (
                      <div key={log?.id} className="border-b border-gray-800 pb-2">
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 text-xs">
                            {new Date(log.timestamp)?.toLocaleTimeString()}
                          </span>
                          <span>{getLogIcon(log?.type)}</span>
                          <span className={`flex-1 ${getLogColor(log?.type)}`}>
                            {log?.message}
                          </span>
                        </div>
                        {log?.data && (
                          <div className="mt-1 ml-20 text-xs text-gray-400">
                            <pre className="whitespace-pre-wrap break-all">
                              {JSON.stringify(log?.data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                    <div ref={logsEndRef} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevDebug;